import { NormalizedAlert } from '../types/alert';

const STORAGE_KEY = 'sn_historical_alerts_24h';
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

interface ArchivedAlertRecord {
  alert: NormalizedAlert;
  firstTrackedAt: number;
  lastActiveAt: number;
  expiredAt: number | null;
  isExpired: boolean;
}

export class AlertHistoryService {
  private static memoryRecords = new Map<string, ArchivedAlertRecord>();

  /**
   * Load archived alert records from storage or memory
   */
  private static loadRecords(): Map<string, ArchivedAlertRecord> {
    if (this.memoryRecords.size > 0) {
      return new Map(this.memoryRecords);
    }

    const map = new Map<string, ArchivedAlertRecord>();
    if (typeof window === 'undefined') return map;

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const list: ArchivedAlertRecord[] = JSON.parse(stored);
        const cutoff = Date.now() - TWENTY_FOUR_HOURS_MS;
        for (const item of list) {
          // Keep only records within past 24h
          if (item.lastActiveAt > cutoff || (item.expiredAt && item.expiredAt > cutoff)) {
            map.set(item.alert.id, item);
          }
        }
      }
    } catch (e) {
      console.warn('Failed to parse 24h alert history:', e);
    }
    return map;
  }

  /**
   * Save records back to memory and localStorage
   */
  private static saveRecords(records: Map<string, ArchivedAlertRecord>): void {
    this.memoryRecords = new Map(records);
    if (typeof window === 'undefined') return;
    try {
      const arr = Array.from(records.values());
      localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
    } catch (e) {
      console.warn('Failed to persist 24h alert history to localStorage:', e);
    }
  }

  /**
   * Sync active alerts with history:
   * 1. Updates records for current active alerts
   * 2. Marks alerts that disappeared or passed expiration date as expired
   */
  static recordAlerts(activeAlerts: NormalizedAlert[]): void {
    const now = Date.now();
    const records = this.loadRecords();
    const activeIds = new Set(activeAlerts.map((a) => a.id));

    // Update or insert current active alerts
    for (const alert of activeAlerts) {
      const existing = records.get(alert.id);
      if (existing) {
        existing.lastActiveAt = now;
        existing.alert = { ...alert, isExpired: false, expiredAt: null };
      } else {
        records.set(alert.id, {
          alert: { ...alert, isExpired: false, expiredAt: null },
          firstTrackedAt: now,
          lastActiveAt: now,
          expiredAt: null,
          isExpired: false,
        });
      }
    }

    // Check for alerts that are no longer in the active list OR whose official expires time passed
    for (const [id, record] of records.entries()) {
      if (!activeIds.has(id)) {
        if (!record.isExpired) {
          record.isExpired = true;
          record.expiredAt = now;
          record.alert.isExpired = true;
          record.alert.expiredAt = new Date(now).toISOString();
        }
      } else {
        // Even if in active list, check if official expires date is in the past
        if (record.alert.expires) {
          const expTime = new Date(record.alert.expires).getTime();
          if (!isNaN(expTime) && expTime < now && !record.isExpired) {
            record.isExpired = true;
            record.expiredAt = expTime;
            record.alert.isExpired = true;
            record.alert.expiredAt = new Date(expTime).toISOString();
          }
        }
      }
    }

    // Prune entries older than 24h
    const cutoff = now - TWENTY_FOUR_HOURS_MS;
    for (const [id, record] of records.entries()) {
      const referenceTime = record.expiredAt || record.lastActiveAt;
      if (referenceTime < cutoff) {
        records.delete(id);
      }
    }

    this.saveRecords(records);
  }

  /**
   * Get all alerts that have expired/resolved within the past 24 hours
   */
  static getExpiredAlerts(): NormalizedAlert[] {
    const records = this.loadRecords();
    const list: NormalizedAlert[] = [];

    for (const record of records.values()) {
      if (record.isExpired) {
        list.push({
          ...record.alert,
          isExpired: true,
          expiredAt: record.expiredAt ? new Date(record.expiredAt).toISOString() : record.alert.expires,
        });
      }
    }

    return list.sort((a, b) => {
      const timeA = new Date(a.expiredAt || a.expires || 0).getTime();
      const timeB = new Date(b.expiredAt || b.expires || 0).getTime();
      return timeB - timeA;
    });
  }

  /**
   * Return a single combined list of active alerts + 24-hour expired alerts
   */
  static getAllAlertsWithHistory(activeAlerts: NormalizedAlert[]): NormalizedAlert[] {
    const expired = this.getExpiredAlerts();
    const activeIds = new Set(activeAlerts.map((a) => a.id));

    // Filter out any expired alert that is currently active
    const cleanExpired = expired.filter((e) => !activeIds.has(e.id));
    return [...activeAlerts, ...cleanExpired];
  }
}

